import React, { FC, useRef, useState } from 'react';
import classNames from 'classnames';
import InfiniteScroll from 'react-infinite-scroller';
import { useDispatch, useSelector } from 'react-redux';
import ConfirmationModalLayout from '../../shared/components/modal/ConfirmationModalLayout';
import { SpocState } from '../../store/authentication/state';
import { SelectionListCommentOut } from '../../store/api';
import { AppState } from '../../store/appState';
import { useComments } from './api/useComments';
import { SelectionlistComment } from './SelectionlistComment';
import { useCommentsBarContext } from './CommentsProvider';
import { useDeleteComment } from './api/useDeleteComment';
import { useTranslation } from 'react-i18next';
import { useCreateComment } from './api/useCreateComment';
import { useEditComment } from './api/useEditComment';
import { globalWarning } from '../../store/notifications/actions';

type Props = { selectionListId: string };

export const SelectionlistCommentsBar: FC<Props> = ({ selectionListId }) => {
    const { t } = useTranslation();
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [, setUserId] = useState('');
    const [commentId, setCommentId] = useState<number | undefined>(undefined);
    const selectionListCommentInputSubmitRef = useRef<HTMLDivElement>(null);
    const commentsListRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const [comment, setComment] = useState('');
    const [multiLineOffset, setMultiLineOffset] = useState(0);
    const { closeCommentsBar, isCommentsBarOpen, animateCommentsBar } = useCommentsBarContext();
    let selectionListBarHeight = 38;
    let headerHeight = 106;
    let headerStickyHeight = 44;
    let entryPaddingTop = 150;

    const authentication = useSelector((state: AppState) => state.authentication);
    const orderlists = useSelector((state: AppState) => state.orderlists);
    const isSticky = useSelector((state: AppState) => state.header.isSticky);
    const {
        comments,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading: isCommentsLoading
    } = useComments({
        listId: selectionListId,
        enabled: isCommentsBarOpen ?? false
    });

    const { mutate } = useDeleteComment();
    const { mutate: createComment } = useCreateComment();
    const { mutate: editComment, isLoading: isEditCommentLoading } = useEditComment();

    function handleFetchComments(page: number, commentId?: number) {
        if (hasNextPage && !isFetchingNextPage) {
            return fetchNextPage({ pageParam: { page, commentId } });
        }
    }

    function onCommentConfirmDelete() {
        mutate({ listId: selectionListId, commentId: commentId! }, { onSuccess: handleHideCommentDeleteConfirmation });
    }

    function onCommentConfirmEdit(commentId, text) {
        if (text) {
            editComment({ listId: selectionListId, commentId, text });
        } else {
            dispatch(globalWarning(t('notifications.onCommentConfirmEdit.warning')));
        }
    }

    function handleShowCommentDeleteConfirmation(userId, commentId) {
        setUserId(userId);
        setCommentId(commentId);
        setShowDeleteConfirmation(true);
    }

    function handleHideCommentDeleteConfirmation() {
        setCommentId(undefined);
        setUserId('');
        setShowDeleteConfirmation(false);
    }

    function onHideCommentsBar() {
        return closeCommentsBar();
    }

    function calcSizeHeight(headersize: number) {
        const subValue = !isNaN(headersize) ? headersize : headerHeight;
        const windowHeight = document.body.clientHeight;
        return { height: windowHeight - subValue!, topMargin: subValue };
    }

    function handleChange(e) {
        const defaultInputSubmitHeight = 85;

        setComment(e.target.value);
        setMultiLineOffset((selectionListCommentInputSubmitRef.current?.offsetHeight ?? 0) - defaultInputSubmitHeight);
    }

    function onCommentSubmit() {
        createComment({ listId: selectionListId, text: comment });
        setComment('');
        setMultiLineOffset(0);
    }

    headerHeight = isSticky ? headerStickyHeight! : headerHeight! - window.pageYOffset;
    if (orderlists.selectionListBarExpanded && (isCommentsBarOpen || animateCommentsBar)) {
        headerHeight += selectionListBarHeight!;
    }
    const commentsBarDimension = calcSizeHeight(headerHeight!);

    const commentsBarSlideOutBool = !isCommentsBarOpen && !isCommentsLoading;
    const containerClasses = classNames('sl-comments-container', {
        commentsBarSlideIn: isCommentsBarOpen,
        commentsBarSlideOut: commentsBarSlideOutBool
    });

    const wrapperClasses = classNames('sl-comments-wrapper', {
        hidden: !isCommentsBarOpen && !animateCommentsBar,
        sticky: isSticky,
        'sl-bar-expand': orderlists.selectionListBarExpanded
    });

    const headerClasses = classNames('sl-comments-header', {
        hidden: !isCommentsBarOpen && !animateCommentsBar,
        'sl-bar-expand': orderlists.selectionListBarExpanded,
        'header-overflow': true
    });

    return (
        <div>
            <ConfirmationModalLayout
                visible={showDeleteConfirmation}
                title={t('components.comments.deleteCommentModalTitle')}
                text={t('components.comments.deleteCommentModalSubtitle')}
                onConfirm={onCommentConfirmDelete}
                onCancel={handleHideCommentDeleteConfirmation}
            />
            <div style={{ height: commentsBarDimension.height }} className={wrapperClasses}>
                <div className={containerClasses} style={{ height: commentsBarDimension.height }}>
                    <div className={headerClasses} style={{ top: commentsBarDimension.topMargin }}>
                        <span className="title">{t('components.comments.title')}</span>
                        <a className="close fl-right" onClick={onHideCommentsBar}>
                            <i className="material-icons">clear</i>
                        </a>
                        <div ref={selectionListCommentInputSubmitRef} className="sl-comment-input-submit">
                            <div className="sl-comment-input-container">
                                <textarea
                                    className="sl-comment-input"
                                    onChange={handleChange}
                                    value={comment}
                                    placeholder={t('components.comments.placeholder')}
                                />
                                <div className="sl-comment-input-size">{comment}</div>
                            </div>
                            <div className="sl-submit-bar">
                                <a
                                    onClick={onCommentSubmit}
                                    className="submit-comment-button fl-right"
                                    hidden={!comment}
                                >
                                    <i className="material-icons">check</i>
                                </a>
                            </div>
                        </div>
                    </div>
                    <div
                        ref={commentsListRef}
                        className="sl-comments-inner"
                        style={{
                            minHeight: commentsBarDimension.height,
                            height: commentsBarDimension.height,
                            overflow: 'auto',
                            paddingTop: entryPaddingTop! + multiLineOffset
                        }}
                    >
                        {comments?.length > 0 ? (
                            <InfiniteScroll
                                loadMore={page => {
                                    const lastCommentId = comments[comments.length - 1].id ?? undefined;
                                    return handleFetchComments(page, lastCommentId);
                                }}
                                initialLoad={false}
                                hasMore={hasNextPage}
                                useWindow={false}
                            >
                                <ul className="sl-comments-entries">
                                    {comments?.map(comment => {
                                        if (comment.editLog) {
                                            return (
                                                <EditLogEntry
                                                    key={comment.id}
                                                    entry={comment}
                                                    spoc={authentication.spoc}
                                                />
                                            );
                                        } else {
                                            return (
                                                <SelectionlistComment
                                                    key={comment.id}
                                                    entry={comment}
                                                    onCommentConfirmEdit={onCommentConfirmEdit}
                                                    showCommentDeleteConfirmation={handleShowCommentDeleteConfirmation}
                                                    authentication={authentication}
                                                    pendingEditRequest={isEditCommentLoading}
                                                />
                                            );
                                        }
                                    })}
                                </ul>
                            </InfiniteScroll>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

type EditLogProps = {
    readonly spoc: SpocState;
    readonly entry: SelectionListCommentOut;
};

const EditLogEntry = (props: EditLogProps) => {
    const { t } = useTranslation();
    const entry = props.entry;
    const library = entry.admin ? <span className="bolinda-tag">Bolinda</span> : <span>{props.spoc.name}</span>;

    return (
        <li className="sl-comments-entry sl-editlog">
            <strong>{entry.userName}</strong>&nbsp;{library}
            <div className="sl-comment-timestamp sl-editlog-text">
                {t('selectionList.comment.editedAt', {
                    dateTime: `${t('dateFormat.date', {
                        val: entry.modDat ?? 0
                    })} ${t('dateFormat.time', {
                        val: entry.modDat ?? 0
                    })}`
                })}
            </div>
        </li>
    );
};
